import type { Vehicle, VehicleEnergy, VehicleTransmission } from '../../data/vehicles'
import type { InventoryMessageKey } from './messages'
import { useInventoryTranslation } from './useInventoryTranslation'

const energyKeys: Record<VehicleEnergy, InventoryMessageKey> = {
  petrol: 'energyPetrol',
  hybrid: 'energyHybrid',
  plugInHybrid: 'energyPlugInHybrid',
  electric: 'energyElectric',
  lpg: 'energyLpg',
  cng: 'energyCng',
  diesel: 'energyDiesel',
  dieselMildHybrid: 'energyDieselMildHybrid',
  petrolMildHybrid: 'energyPetrolMildHybrid',
}

const transmissionKeys: Record<VehicleTransmission, InventoryMessageKey> = {
  automatic: 'transmissionAutomatic',
  manual: 'transmissionManual',
}

export function useVehicleFactLabels() {
  const { language, t } = useInventoryTranslation()
  const numberLocale = language === 'it' ? 'it-IT' : 'en-GB'

  return {
    energy: (value: VehicleEnergy) => t(energyKeys[value]),
    mileage: (value: number) => `${new Intl.NumberFormat(numberLocale).format(value)} km`,
    money: (value: number) => new Intl.NumberFormat(numberLocale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value),
    transmission: (value: VehicleTransmission) => t(transmissionKeys[value]),
  }
}

export function VehicleFacts({ vehicle, compact = false }: { vehicle: Vehicle; compact?: boolean }) {
  const { t } = useInventoryTranslation()
  const labels = useVehicleFactLabels()
  const { facts } = vehicle
  const rows = [
    facts.year ? { label: t('factsYear'), value: String(facts.year) } : null,
    facts.energy ? { label: t('factsEnergy'), value: labels.energy(facts.energy) } : null,
    typeof facts.mileageKm === 'number'
      ? { label: t('factsMileage'), value: labels.mileage(facts.mileageKm) }
      : null,
    facts.transmission ? { label: t('factsTransmission'), value: labels.transmission(facts.transmission) } : null,
    facts.price ? { label: t('factsPrice'), value: labels.money(facts.price.amountEuro) } : null,
    facts.monthlyPayment
      ? { label: t('factsMonthlyPayment'), value: labels.money(facts.monthlyPayment.amountEuro) }
      : null,
  ].filter((row): row is { label: string; value: string } => Boolean(row))

  return (
    <dl className={`vehicle-facts${compact ? ' vehicle-facts--compact' : ''}`}>
      {rows.map((row) => (
        <div key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  )
}
